import { useState, useRef, useCallback, useEffect, Suspense } from 'react';
import toast from 'react-hot-toast';
// Emoji picker + dataset are code-split; shared with ReactionBar.
import { LazyEmojiPicker as EmojiPickerLazy } from '../ui/LazyEmojiPicker';
import { getSocket, useSocketStore } from '../../lib/socket';
import { ErrorCodes, SocketEvents, syncChannelEpochKey } from '@clinikchat/shared';
import api from '../../lib/api';
import FilePreview from '../file/FilePreview';
import UploadProgress from '../file/UploadProgress';
import {
  encryptMessage,
  encryptDM,
  sealEncryptFileBytes,
  epochKeyRing,
  encodeBase64,
} from '@clinikchat/shared';
import { encryptFileBytesAsync } from '../../lib/crypto-worker';
import { useCryptoStore } from '../../stores/cryptoStore';

interface StagedFile {
  file: File;
  preview?: string;
}

interface UploadState {
  filename: string;
  progress: number;
}

interface MessageInputProps {
  channelId: string;
  channelType: string;
  dmPeerUserId?: string | null;
  editingMessage?: { id: string; content: string } | null;
  onCancelEdit?: () => void;
}

const MAX_FILE_SIZE = 25 * 1024 * 1024;

export default function MessageInput({
  channelId,
  channelType,
  dmPeerUserId,
  editingMessage,
  onCancelEdit,
}: MessageInputProps) {
  const [content, setContent] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [uploads, setUploads] = useState<UploadState[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [peerPublicKey, setPeerPublicKey] = useState<string | null>(null);
  const [restoringKey, setRestoringKey] = useState(false);
  const typingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTyping = useRef(false);
  // Recent sends by correlation id, so a write the server refuses because the
  // channel key moved on hands back exactly THAT draft (the API echoes the id).
  const pendingSendsRef = useRef(new Map<string, { text: string; at: number }>());
  const socketId = useSocketStore((s) => s.socketId);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emojiRef = useRef<HTMLDivElement>(null);

  // Auto-grow the composer with its content instead of pinning it to one
  // line: height tracks scrollHeight until the CSS max-height cap kicks in,
  // after which the textarea scrolls internally. Keyed on `content` so every
  // path that changes it (typing, emoji insert, edit prefill, clear-on-send)
  // resizes without each call site remembering to.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [content]);

  const secretKey = useCryptoStore((s) => s.secretKey);
  const publicKey = useCryptoStore((s) => s.publicKey);
  const setChannelKey = useCryptoStore((s) => s.setChannelKey);
  // Group channels encrypt under the epoch key ring (filled by useChannelEpochKey /
  // syncChannelEpochKey). The legacy flat key is a client-side mirror that can
  // diverge from epoch 1's key, so it is used ONLY for a legacy group the server
  // reports has no epoch rows at all (see `legacyNoEpochs`): those groups were
  // always sent flat-key / no keyEpoch, and every member decrypts that row with
  // their flat key (decryptWithEpoch's legacy fallback). Once any epoch exists the
  // ring key is required.
  // Re-evaluate the ring on each render so canEncrypt flips on once
  // useChannelEpochKey populates it (the hook fires async on mount).
  const ringLatest = epochKeyRing.getLatest(channelId);
  const flatKey = useCryptoStore((s) => s.channelKeys[channelId]);
  // Keyed by channel: a confirmation for one channel can never apply to another,
  // even for the render(s) between a channel switch and the new check returning.
  const [legacyCheck, setLegacyCheck] = useState<{ channelId: string; noEpochs: boolean }>({
    channelId: '',
    noEpochs: false,
  });
  const legacyNoEpochs = legacyCheck.channelId === channelId && legacyCheck.noEpochs;
  // Read at send time (after awaits), so it must not lag a render behind.
  const legacyNoEpochsRef = useRef(false);
  legacyNoEpochsRef.current = legacyNoEpochs && !ringLatest;

  const isPublic = channelType === 'PUBLIC';
  const isDm = channelType === 'DM';
  // A rotation this device has not caught up with: the latest cached key is
  // superseded and a removed member still holds it, so sending is off until the
  // announced epoch's key lands (the epoch hook fetches it and retries).
  const keysBehind = !isPublic && !isDm && epochKeyRing.isBehind(channelId);
  const legacyFlatOk = !isPublic && !isDm && !ringLatest && !!flatKey && legacyNoEpochs;
  const canEncrypt = isPublic
    ? true
    : isDm
      ? !!(peerPublicKey && publicKey && secretKey)
      : (!!ringLatest && !keysBehind) || legacyFlatOk;

  // A flat key and no ring key: ask the server whether the channel has epochs. Only
  // a CONFIRMED empty list enables flat-key sending; an error or any epoch keeps it off.
  const needsLegacyCheck = !isPublic && !isDm && !ringLatest && !!flatKey;
  useEffect(() => {
    if (!needsLegacyCheck) {
      setLegacyCheck({ channelId, noEpochs: false });
      return;
    }
    let cancelled = false;
    setLegacyCheck({ channelId, noEpochs: false });
    api
      .get<{ data: unknown[] | null }>(`/api/v1/channels/${channelId}/keys/epochs`)
      .then(({ data }) => {
        if (!cancelled) setLegacyCheck({ channelId, noEpochs: (data.data ?? []).length === 0 });
      })
      .catch(() => {
        if (!cancelled) setLegacyCheck({ channelId, noEpochs: false });
      });
    return () => {
      cancelled = true;
    };
  }, [needsLegacyCheck, channelId]);

  /**
   * The key a group send must use RIGHT NOW. Call it after every await: the ring
   * can be cleared (leave, removal, logout) while a file is being read, and a
   * result captured earlier would then be stale. Null means refuse to send.
   */
  function resolveSendKey(): { keyB64: string; epoch: number | null } | null {
    if (epochKeyRing.isBehind(channelId)) return null;
    const latest = epochKeyRing.getLatest(channelId);
    if (latest) return { keyB64: encodeBase64(latest.key), epoch: latest.epoch };
    const flat = useCryptoStore.getState().channelKeys[channelId];
    if (flat && legacyNoEpochsRef.current) return { keyB64: flat, epoch: null };
    return null;
  }

  // Generic blocker copy. Failure modes: no epoch key in the ring yet (the
  // Restore-encryption button resolves it via /keys/pending) or DM peer key missing.
  const sendBlockedReason: string | null = (() => {
    if (isPublic || canEncrypt) return null;
    if (isDm) return 'Encryption keys are not ready. Please reload or log in again.';
    if (keysBehind) {
      return 'Channel keys are being updated after a membership change. Sending resumes as soon as this device has the new key — tap Restore encryption if it does not.';
    }
    return "Setting up encryption… If this doesn't clear, tap Restore encryption, or ask a channel admin to open the group to upgrade its encryption.";
  })();

  // "Restore encryption": runs the same sync the epoch hook does, on demand (see
  // syncChannelEpochKey). A device without its secret key has to sign in again.
  const handleRestoreKey = useCallback(async () => {
    if (restoringKey) return;
    setRestoringKey(true);
    try {
      if (!secretKey) {
        toast.error(
          "Your device's encryption key isn't loaded. Sign out and sign back in on this device to restore it.",
        );
        return;
      }
      if (isDm) {
        // DMs have no shared channel key; re-fetch the peer's public key and
        // trigger a re-render via state bump.
        if (dmPeerUserId) {
          const { data } = await api.get<{
            success: true;
            data: { publicKey: string | null };
          }>(`/api/v1/users/${dmPeerUserId}/public-key`);
          setPeerPublicKey(data.data?.publicKey ?? null);
          if (!data.data?.publicKey) {
            toast.error('The other person has not set up encryption yet.');
          } else {
            toast.success('Encryption restored');
          }
        }
        return;
      }
      // The same pass the epoch hook runs: pick up and acknowledge a pending key,
      // resolve the current epoch, fetch (or, as an admin, distribute) its key.
      const outcome = await syncChannelEpochKey(
        {
          api,
          secretKey,
          onKeyDistributed: (id, keyB64) => setChannelKey(id, keyB64),
          legacyFlatKey: (id) => useCryptoStore.getState().channelKeys[id],
        },
        channelId,
        { bootstrapFirstEpoch: true, ignoreRefusals: true },
      );
      if (outcome.outcome === 'dropped') return; // signed out mid-request
      if (outcome.outcome === 'ready' && outcome.key) {
        // Bump the crypto store so canEncrypt re-evaluates (the ring is not reactive).
        setChannelKey(channelId, encodeBase64(outcome.key));
        // Only claim success for a key the server confirmed as current: after a
        // failed epoch list the ring's own, possibly superseded, key comes back.
        if (outcome.confirmed && !epochKeyRing.isBehind(channelId)) {
          toast.success('Encryption restored');
        } else {
          toast.error(
            "Couldn't confirm the latest channel key. Check your connection and try again.",
          );
        }
      } else if (outcome.outcome === 'failed') {
        toast.error(
          "We couldn't restore the encryption key with your device key. Try signing out and back in.",
        );
      } else {
        toast.error(
          'No channel key has been shared with you yet. Ask a channel owner or admin to trigger a key rotation for this channel, then try again.',
        );
      }
    } catch {
      toast.error("Couldn't restore the encryption key. Try signing out and back in.");
    } finally {
      setRestoringKey(false);
    }
  }, [restoringKey, secretKey, isDm, dmPeerUserId, channelId, setChannelKey]);

  // The server refuses writes under an epoch a departed member holds. The epoch
  // hook fetches the new key; here the just-sent text goes back into the composer.
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onError = (payload: { code?: string; channelId?: string; clientId?: string }) => {
      if (payload?.code !== ErrorCodes.KEY_EPOCH_SUPERSEDED || payload.channelId !== channelId) {
        return;
      }
      const sent = payload.clientId ? pendingSendsRef.current.get(payload.clientId) : undefined;
      if (!payload.clientId || !sent) return;
      pendingSendsRef.current.delete(payload.clientId);
      // Never overwrite text typed since.
      setContent((current) => current || sent.text);
    };
    socket.on(SocketEvents.ERROR, onError);
    return () => {
      socket.off(SocketEvents.ERROR, onError);
    };
  }, [channelId, socketId]);

  // Fetch DM peer public key when the peer changes
  useEffect(() => {
    if (!isDm || !dmPeerUserId) {
      setPeerPublicKey(null);
      return;
    }
    let cancelled = false;
    api
      .get<{ success: true; data: { publicKey: string | null } }>(
        `/api/v1/users/${dmPeerUserId}/public-key`,
      )
      .then(({ data }) => {
        if (!cancelled) setPeerPublicKey(data.data?.publicKey ?? null);
      })
      .catch(() => {
        if (!cancelled) setPeerPublicKey(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isDm, dmPeerUserId]);

  useEffect(() => {
    if (editingMessage) {
      setContent(editingMessage.content);
      textareaRef.current?.focus();
    }
  }, [editingMessage]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) {
        setShowEmoji(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const emitTypingStart = useCallback(() => {
    const socket = getSocket();
    if (!socket || isTyping.current) return;
    isTyping.current = true;
    socket.emit(SocketEvents.TYPING_START, { channelId });
  }, [channelId]);

  const emitTypingStop = useCallback(() => {
    const socket = getSocket();
    if (!socket || !isTyping.current) return;
    isTyping.current = false;
    socket.emit(SocketEvents.TYPING_STOP, { channelId });
  }, [channelId]);

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setContent(e.target.value);
    if (!editingMessage) {
      emitTypingStart();
      if (typingTimeout.current) clearTimeout(typingTimeout.current);
      typingTimeout.current = setTimeout(() => emitTypingStop(), 3000);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage();
    }
    if (e.key === 'Escape' && editingMessage && onCancelEdit) {
      onCancelEdit();
      setContent('');
    }
  }

  // A rotation this device has not caught up with: the latest cached key is
  // superseded and a removed member still holds it (the API accepts any known
  // epoch). Refuse to encrypt anything until the announced epoch's key lands;
  // the draft and staged files are kept.
  function blockedByRotation(): boolean {
    if (channelType === 'PUBLIC' || channelType === 'DM') return false;
    if (!epochKeyRing.isBehind(channelId)) return false;
    // One toast even when this fires again after an upload attempt.
    toast.error(
      'Channel keys are being updated after a membership change. Your message has been kept — please try again in a moment.',
      { id: 'rotation-blocked' },
    );
    return true;
  }

  async function sendMessage() {
    const trimmed = content.trim();

    if (blockedByRotation()) return;

    // No key for the current epoch yet (e.g. a channel still setting up): refuse
    // BEFORE any upload, which would otherwise fall through to plaintext.
    if (!isPublic && !canEncrypt) {
      toast.error(sendBlockedReason ?? 'Cannot send: encryption key unavailable.');
      return;
    }

    if (stagedFiles.length > 0) {
      await uploadFiles();
    }

    if (!trimmed) {
      if (stagedFiles.length === 0) return;
      return;
    }

    const socket = getSocket();
    if (!socket) return;

    // Uploads above awaited: a rotation may have been announced meanwhile.
    if (blockedByRotation()) return;

    // Re-resolve the key NOW, after every await. A private channel with no usable
    // key (cleared by leave / removal / logout meanwhile) refuses: never plaintext.
    const dmReady = isDm && !!peerPublicKey && !!publicKey && !!secretKey;
    const groupKey = !isPublic && !isDm ? resolveSendKey() : null;
    if (!isPublic && !(isDm ? dmReady : !!groupKey)) {
      toast.error(sendBlockedReason ?? 'Cannot send: encryption key unavailable.');
      return;
    }

    let outgoingContent = trimmed;
    let isEncrypted = false;
    // Stamp the epoch when we encrypt with an epoch-ring key so the server records
    // which key produced the ciphertext. Only a legacy no-epoch group sends null.
    let outgoingKeyEpoch: number | null = null;

    if (isDm) {
      outgoingContent = JSON.stringify(encryptDM(trimmed, peerPublicKey!, publicKey!));
      isEncrypted = true;
    } else if (groupKey) {
      outgoingContent = encryptMessage(trimmed, groupKey.keyB64);
      isEncrypted = true;
      outgoingKeyEpoch = groupKey.epoch;
    }

    if (editingMessage) {
      socket.emit(SocketEvents.MESSAGE_EDIT, {
        messageId: editingMessage.id,
        content: outgoingContent,
        encrypted: isEncrypted,
        ...(outgoingKeyEpoch !== null ? { keyEpoch: outgoingKeyEpoch } : {}),
      });
      onCancelEdit?.();
    } else {
      const clientId = globalThis.crypto.randomUUID();
      const pending = pendingSendsRef.current;
      const now = Date.now();
      for (const [id, sent] of pending) if (now - sent.at > 30_000) pending.delete(id);
      pending.set(clientId, { text: trimmed, at: now });
      socket.emit(SocketEvents.MESSAGE_NEW, {
        channelId,
        clientId,
        content: outgoingContent,
        encrypted: isEncrypted,
        ...(outgoingKeyEpoch !== null ? { keyEpoch: outgoingKeyEpoch } : {}),
      });
    }

    setContent('');
    emitTypingStop();
    if (typingTimeout.current) clearTimeout(typingTimeout.current);
  }

  async function uploadFiles() {
    const uploaded = new Set<File>();
    for (const sf of stagedFiles) {
      setUploads((prev) => [...prev, { filename: sf.file.name, progress: 0 }]);

      try {
        const arrayBuf = await sf.file.arrayBuffer();
        const plainBytes = new Uint8Array(arrayBuf);
        // The read is slow; a rotation may have been announced during it.
        if (blockedByRotation()) {
          // Files already sent must not stay staged (a retry would upload them
          // again); the rest are kept for the retry, and no progress rows linger.
          setStagedFiles((prev) => prev.filter((f) => !uploaded.has(f.file)));
          setUploads([]);
          return;
        }

        // DM channel: dual-blob sealed-box upload. Each blob is sealed for one
        // party so neither S3 admin nor the server can read the plaintext.
        if (channelType === 'DM' && peerPublicKey && publicKey) {
          const recipientCiphertextB64 = sealEncryptFileBytes(plainBytes, peerPublicKey);
          const senderCiphertextB64 = sealEncryptFileBytes(plainBytes, publicKey);

          const recipientBytes = Uint8Array.from(atob(recipientCiphertextB64), (c) =>
            c.charCodeAt(0),
          );
          const senderBytes = Uint8Array.from(atob(senderCiphertextB64), (c) => c.charCodeAt(0));

          const form = new FormData();
          form.append(
            'recipientBlob',
            new File([recipientBytes], 'encrypted.bin', { type: 'application/octet-stream' }),
          );
          form.append(
            'senderBlob',
            new File([senderBytes], 'encrypted.bin', { type: 'application/octet-stream' }),
          );
          form.append('filename', sf.file.name);

          await api.post(`/api/v1/channels/${channelId}/dm-upload`, form, {
            onUploadProgress: (e) => {
              const pct = Math.round((e.loaded * 100) / (e.total || 1));
              setUploads((prev) =>
                prev.map((u) => (u.filename === sf.file.name ? { ...u, progress: pct } : u)),
              );
            },
          });
        } else if (isPublic) {
          // PUBLIC channels are the only place a plaintext upload is allowed.
          const form = new FormData();
          form.append('file', sf.file);

          await api.post(`/api/v1/channels/${channelId}/upload`, form, {
            onUploadProgress: (e) => {
              const pct = Math.round((e.loaded * 100) / (e.total || 1));
              setUploads((prev) =>
                prev.map((u) => (u.filename === sf.file.name ? { ...u, progress: pct } : u)),
              );
            },
          });
        } else {
          // PRIVATE channel: symmetric encryption. The key is resolved HERE, after
          // the file read above, because leave / removal / logout may have cleared
          // it meanwhile. No key (or a DM without its keys) refuses: never plaintext.
          const sendKey = isDm ? null : resolveSendKey();
          if (!sendKey) {
            toast.error(sendBlockedReason ?? 'Cannot send: encryption key unavailable.');
            setStagedFiles((prev) => prev.filter((f) => !uploaded.has(f.file)));
            setUploads([]);
            return;
          }
          const encryptionKey = sendKey.keyB64;
          const uploadEpoch = sendKey.epoch;

          const encryptedB64 = await encryptFileBytesAsync(plainBytes, encryptionKey);
          const encryptedBytes = Uint8Array.from(atob(encryptedB64), (c) => c.charCodeAt(0));
          const uploadFile = new File([encryptedBytes], 'encrypted.bin', {
            type: 'application/octet-stream',
          });

          const form = new FormData();
          form.append('file', uploadFile);
          form.append('encrypted', 'true');
          form.append('encryptedFilename', encryptMessage(sf.file.name, encryptionKey));
          form.append(
            'encryptedMimeType',
            encryptMessage(sf.file.type || 'application/octet-stream', encryptionKey),
          );
          form.append('originalSize', String(sf.file.size));
          if (uploadEpoch !== null) form.append('keyEpoch', String(uploadEpoch));

          await api.post(`/api/v1/channels/${channelId}/upload`, form, {
            onUploadProgress: (e) => {
              const pct = Math.round((e.loaded * 100) / (e.total || 1));
              setUploads((prev) =>
                prev.map((u) => (u.filename === sf.file.name ? { ...u, progress: pct } : u)),
              );
            },
          });
        }
        uploaded.add(sf.file);
      } catch {
        toast.error(`Failed to upload ${sf.file.name}`);
      }
    }

    setStagedFiles([]);
    setTimeout(() => setUploads([]), 1000);
  }

  function stageFiles(files: FileList | File[]) {
    const arr = Array.from(files);
    const toStage: StagedFile[] = [];

    for (const f of arr) {
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`${f.name} exceeds 25MB limit`);
        continue;
      }

      const sf: StagedFile = { file: f };
      if (f.type.startsWith('image/')) {
        sf.preview = URL.createObjectURL(f);
      }
      toStage.push(sf);
    }

    setStagedFiles((prev) => [...prev, ...toStage]);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      stageFiles(e.dataTransfer.files);
    }
  }

  function handleEmojiSelect(emoji: { native: string }) {
    setContent((prev) => prev + emoji.native);
    setShowEmoji(false);
    textareaRef.current?.focus();
  }

  const sendDisabled = (!content.trim() && stagedFiles.length === 0) || (!isPublic && !canEncrypt);

  return (
    <div
      className="relative"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-primary-light/50">
          <p className="text-h3 font-semibold text-primary">Drop files here</p>
        </div>
      )}

      {editingMessage && (
        <div className="flex items-center gap-2 border-t border-primary-light bg-primary-light px-6 py-2 text-body-sm text-primary">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
          Editing message
          <button
            onClick={() => {
              onCancelEdit?.();
              setContent('');
            }}
            className="ml-auto text-primary hover:text-primary-hover"
          >
            Cancel
          </button>
        </div>
      )}

      {!isPublic && !canEncrypt && (
        <div
          id={`send-blocked-reason-${channelId}`}
          className="flex items-center gap-3 border-t border-red-200 bg-red-50 px-6 py-2 text-caption text-red-600 dark:border-red-900 dark:bg-red-950/40 dark:text-red-400"
        >
          <span className="flex-1">
            {sendBlockedReason ?? "This channel's encryption key isn't loaded on this device."}
          </span>
          <button
            type="button"
            onClick={handleRestoreKey}
            disabled={restoringKey}
            className="shrink-0 rounded-md border border-red-300 bg-white px-2 py-1 font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60 dark:border-red-800 dark:bg-red-950/60 dark:text-red-200 dark:hover:bg-red-900/60"
          >
            {restoringKey ? 'Restoring…' : 'Restore encryption'}
          </button>
        </div>
      )}

      <FilePreview
        files={stagedFiles}
        onRemove={(i) => setStagedFiles((prev) => prev.filter((_, idx) => idx !== i))}
      />
      <UploadProgress uploads={uploads} />

      <div className="border-t border-warm-border-light px-3 py-3 dark:border-warm-dark-border md:px-5">
        {/* Below sm the composer wraps to two rows (textbox, then icon controls) so the placeholder is not clipped at 320px. */}
        <div className="flex flex-wrap items-end gap-y-2 rounded-xl border border-warm-border bg-warm-white p-2 shadow-warm-sm focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 dark:border-warm-dark-border dark:bg-warm-dark-surface sm:flex-nowrap">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            // order-2 at <sm so this icon sits on the second row alongside Send,
            // textarea takes the full first row.
            className="order-2 mr-1 rounded-lg p-2 text-warm-gray-400 hover:bg-warm-gray-100 hover:text-primary dark:hover:bg-white/10 dark:hover:text-slate-200 sm:order-1 md:p-1.5"
            aria-label="Attach file"
            title="Attach file"
          >
            <svg
              className="h-5 w-5 md:h-4 md:w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
              />
            </svg>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.rtf"
            className="hidden"
            onChange={(e) => {
              if (e.target.files) stageFiles(e.target.files);
              e.target.value = '';
            }}
          />

          <textarea
            ref={textareaRef}
            value={content}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              // On iOS, scroll the input into view after the keyboard opens
              setTimeout(() => textareaRef.current?.scrollIntoView({ block: 'nearest' }), 300);
            }}
            placeholder={
              !isPublic && !canEncrypt
                ? isDm
                  ? 'Encryption unavailable — cannot send'
                  : 'Setting up encryption…'
                : editingMessage
                  ? 'Edit your message...'
                  : 'Type a message...'
            }
            rows={1}
            disabled={!isPublic && !canEncrypt}
            aria-label="Message"
            // basis-full at narrow widths forces the textarea to take a row of its own
            // (icons wrap to the next row), then at >=sm we collapse back to a single row.
            className="order-1 max-h-[40vh] w-full resize-none basis-full overflow-y-auto bg-transparent px-2 py-1.5 text-body text-warm-gray-800 placeholder:text-warm-gray-400 outline-none disabled:opacity-50 dark:text-white dark:placeholder:text-warm-gray-500 sm:order-2 sm:w-auto sm:flex-1 sm:basis-auto"
            style={{ minHeight: '24px' }}
          />

          <div className="relative order-3" ref={emojiRef}>
            <button
              type="button"
              onClick={() => setShowEmoji(!showEmoji)}
              className="mx-1 hidden rounded-lg p-2 text-warm-gray-400 hover:bg-warm-gray-100 hover:text-primary dark:hover:bg-white/10 dark:hover:text-slate-200 sm:block md:p-1.5"
              aria-label="Emoji picker"
              aria-expanded={showEmoji}
              title="Emoji"
            >
              <svg
                className="h-5 w-5 md:h-4 md:w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </button>
            {showEmoji && (
              <div className="absolute bottom-12 right-0 z-20">
                <Suspense
                  fallback={<div className="h-80 w-72 rounded-lg bg-warm-white shadow-warm-md" />}
                >
                  <EmojiPickerLazy onEmojiSelect={handleEmojiSelect} />
                </Suspense>
              </div>
            )}
          </div>

          <button
            onClick={() => void sendMessage()}
            disabled={sendDisabled}
            aria-label="Send message"
            aria-describedby={
              !isPublic && !canEncrypt ? `send-blocked-reason-${channelId}` : undefined
            }
            // order-4 keeps Send last on the icon row at every width.
            className="order-4 ml-auto flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg bg-primary text-white transition hover:bg-primary-hover disabled:opacity-30 sm:min-h-0 sm:min-w-0 sm:p-2"
          >
            <svg
              className="h-5 w-5 md:h-4 md:w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
